/**
 * Copy-approval-chain-from-another-project picker.
 *
 * A two-phase in-dialog flow:
 *   1. `project`  — search + pick a source project (all workspace projects the
 *                   user can read, minus the current one).
 *   2. `approval-chain` — pick one of that project's approval chain definitions.
 *
 * On confirm we fetch the source approval chain's detail, strip it down for the new
 * project (no assignees, auto-apply reset — see `mapCopiedApprovalChainToFormData`),
 * and hand the prefill to the caller, which opens the editor panel with it.
 * Nothing persists until the user reviews + saves there, so the BE `copy-from`
 * endpoint is intentionally not used by this flow.
 */
import { useMemo, useState } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeftIcon, ChevronRightIcon } from 'lucide-react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Spinner,
} from '@monksflow/monks-ui';

import { projectsInfiniteQuery } from '@/entities/project';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  useApprovalChains,
  approvalChainQuery,
  type ApprovalChain,
} from '@/entities/approval-chain';
import { SEARCH_DEBOUNCE_MS } from '@/shared/constants/search.constants';
import { useDebounce } from '@/shared/hooks/useDebounce/useDebounce';
import { notificationService } from '@/lib/services/notification.service';
import { parseProblemDetail } from '@/shared/utils/problem-details';
import { SearchInput } from '@/shared/ui/SearchInput';

import { mapCopiedApprovalChainToFormData, type ApprovalChainFormInput } from '../../model/schema';
import { useTranslation } from 'react-i18next';

export interface CopyApprovalChainDialogProps {
  open: boolean;
  /**
   * Platform project id of the project the copy lands in — excluded from the
   * source picker and used for the new-approval-chain route (both project-scoped).
   */
  targetProjectId: string;
  onClose: () => void;
  /**
   * Receives the stripped-down copy so the caller can open the editor with it.
   * The prefill is handed over in memory rather than through router state,
   * because the editor is a panel on this same page now — there is no
   * navigation to carry it.
   */
  onCopied: (prefill: ApprovalChainFormInput, sourceName: string) => void;
}

type Phase = 'project' | 'approval-chain';

export function CopyApprovalChainDialog({
  open,
  targetProjectId,
  onClose,
  onCopied,
}: CopyApprovalChainDialogProps) {
  const { t } = useTranslation('approvalChains');

  const queryClient = useQueryClient();
  const workspaceId = useCurrentWorkspaceId();

  // parent keys this dialog on `open`, so fresh state on each
  // (re)open is guaranteed by remount — no reset effect needed.
  const [phase, setPhase] = useState<Phase>('project');
  const [source, setSource] = useState<{ id: string; title: string } | null>(null);
  const [search, setSearch] = useState('');
  // Id of the approval chain whose detail is being fetched (drives the row spinner).
  const [copyingApprovalChainId, setCopyingApprovalChainId] = useState<string | null>(null);

  /** Drafts can have a null title; show a stable placeholder so rows stay readable. */
  const untitledProject = t('manageApprovalChains.copyApprovalChainDialog.untitledProject');

  const debouncedSearch = useDebounce(search, SEARCH_DEBOUNCE_MS);
  const trimmedSearch = debouncedSearch.trim();

  const {
    data: projectPages,
    isLoading: isLoadingProjects,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    ...projectsInfiniteQuery(workspaceId, {
      sortBy: 'title',
      sortOrder: 'ASC',
      search: trimmedSearch || undefined,
    }),
    enabled: open && !!workspaceId,
  });

  // Source projects must be addressable by platform id (approval chain routes are
  // scoped to it), so drop drafts (no platform project) and exclude the target.
  const projects = useMemo(
    () =>
      (projectPages?.pages.flatMap((page) => page.items) ?? []).filter(
        (project): project is typeof project & { platformProjectId: string } =>
          !!project.platformProjectId && project.platformProjectId !== targetProjectId,
      ),
    [projectPages, targetProjectId],
  );

  const { data: approvalChainsResponse, isLoading: isLoadingApprovalChains } = useApprovalChains(
    phase === 'approval-chain' ? source?.id : undefined,
  );
  const approvalChains = approvalChainsResponse?.items ?? [];

  const handleClose = () => {
    if (copyingApprovalChainId) return; // don't close mid-fetch
    onClose();
  };

  const handleSelectProject = (id: string, title: string) => {
    setSource({ id, title });
    setPhase('approval-chain');
  };

  const handleSelectApprovalChain = async (approvalChain: ApprovalChain) => {
    if (!source || copyingApprovalChainId) return;
    setCopyingApprovalChainId(approvalChain.id);
    try {
      const detail = await queryClient.fetchQuery(approvalChainQuery(source.id, approvalChain.id));
      const approvalChainPrefill = mapCopiedApprovalChainToFormData(detail);
      onClose();
      onCopied(approvalChainPrefill, approvalChain.name);
    } catch (error) {
      const { detail } = parseProblemDetail(
        error,
        t('manageApprovalChains.copyApprovalChainDialog.failedToCopyApprovalChain'),
      );
      notificationService.showError(detail);
      setCopyingApprovalChainId(null);
    }
  };

  const title =
    phase === 'project'
      ? t('manageApprovalChains.copyApprovalChainDialog.copyFromAnotherProject')
      : t('manageApprovalChains.copyApprovalChainDialog.approvalChainsIn', {
          project: source?.title ?? '',
        });

  return (
    <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
      <DialogContent className="max-w-140 gap-0 p-4" data-testid="copy-approval-chain-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {phase === 'approval-chain' && (
              <Button
                type="button"
                variant="ghost"
                size="mini"
                className="size-8 p-0"
                onClick={() => setPhase('project')}
                disabled={!!copyingApprovalChainId}
                aria-label={t('manageApprovalChains.copyApprovalChainDialog.backToProjects')}
                data-testid="copy-approval-chain-back"
              >
                <ArrowLeftIcon className="size-4" />
              </Button>
            )}
            {title}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3 pt-4">
          {phase === 'project' ? (
            <>
              <SearchInput
                placeholder={t('manageApprovalChains.copyApprovalChainDialog.searchProjects')}
                aria-label={t('manageApprovalChains.copyApprovalChainDialog.searchProjectsLabel')}
                value={search}
                onChange={setSearch}
              />

              <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
                {isLoadingProjects ? (
                  <div className="flex justify-center py-10">
                    <Spinner />
                  </div>
                ) : projects.length === 0 ? (
                  <p className="text-foreground-alt py-10 text-center text-sm">
                    {t('manageApprovalChains.copyApprovalChainDialog.noOtherProjectsFound')}
                  </p>
                ) : (
                  <>
                    {projects.map((project) => (
                      <button
                        key={project.id}
                        type="button"
                        onClick={() =>
                          handleSelectProject(
                            project.platformProjectId,
                            project.title ?? untitledProject,
                          )
                        }
                        className="hover:bg-accent flex items-center justify-between rounded-md px-3 py-2 text-left text-sm"
                        data-testid={`copy-approval-chain-project-${project.id}`}
                      >
                        <span className="truncate">{project.title ?? untitledProject}</span>
                        <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" />
                      </button>
                    ))}
                    {hasNextPage && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => void fetchNextPage()}
                        disabled={isFetchingNextPage}
                        data-testid="copy-approval-chain-project-load-more"
                      >
                        {isFetchingNextPage
                          ? t('manageApprovalChains.copyApprovalChainDialog.loading')
                          : t('manageApprovalChains.copyApprovalChainDialog.loadMore')}
                      </Button>
                    )}
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex max-h-80 flex-col gap-1 overflow-y-auto">
              {isLoadingApprovalChains ? (
                <div className="flex justify-center py-10">
                  <Spinner />
                </div>
              ) : approvalChains.length === 0 ? (
                <p className="text-foreground-alt py-10 text-center text-sm">
                  {t('manageApprovalChains.copyApprovalChainDialog.thisProjectHasNoApprovalChains')}
                </p>
              ) : (
                approvalChains.map((approvalChain) => (
                  <button
                    key={approvalChain.id}
                    type="button"
                    onClick={() => void handleSelectApprovalChain(approvalChain)}
                    disabled={!!copyingApprovalChainId}
                    className="hover:bg-accent flex items-center justify-between rounded-md px-3 py-2 text-left text-sm disabled:opacity-60"
                    data-testid={`copy-approval-chain-approval-chain-${approvalChain.id}`}
                  >
                    <span className="flex flex-col">
                      <span className="truncate font-medium">{approvalChain.name}</span>
                      <span className="text-foreground-alt text-xs">
                        {t('manageApprovalChains.copyApprovalChainDialog.stepCount', {
                          count: approvalChain.stepCount,
                        })}
                      </span>
                    </span>
                    {copyingApprovalChainId === approvalChain.id ? (
                      <Spinner className="size-4" />
                    ) : (
                      <ChevronRightIcon className="text-muted-foreground size-4 shrink-0" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

CopyApprovalChainDialog.displayName = 'CopyApprovalChainDialog';
